import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pill } from '@/components/widgets/Pill';
import { adminSymbols } from '@/api/admin';
import { fmtMoney, fmtNum, fmtTime, moneyToNumber } from '@/lib/format';
import type { Money, Signal } from '@/api/types';
import { SignalTimeline } from './SignalTimeline';

// 与 RecentSignalsStream / SignalRow 同口径(v2 状态枚举)。
const STATUS_LABEL: Record<Signal['status'], string> = {
  executed: '已成交',
  skipped_user_status: '用户停用',
  skipped_risk: '风控拦截',
  skipped_session: '时段拦截',
  failed: '失败',
};
const STATUS_TONE: Record<Signal['status'], 'up' | 'down' | 'warn' | 'neutral'> = {
  executed: 'up',
  skipped_user_status: 'neutral',
  skipped_risk: 'warn',
  skipped_session: 'warn',
  failed: 'down',
};

const lots = (n: number) => (n / 100).toFixed(2);

interface Props {
  /** 非空 = 打开弹窗展示该信号;null = 关闭。 */
  signal: Signal | null;
  onClose: () => void;
}

/**
 * 跟单流水「历史详情」弹窗(管理端,§9.8)。从「当日跟单流水」行下钻打开。
 *
 * 管理端内部直说真实 / 反向 / 比例,与 App 雇员端伪装无关。三段:
 * 1) 雇员单(虚拟意图)userSide/userVolume;2) 主账户真实腿 realSide/realVolume + 模式·比例 + 真实订单/仓 id;
 * 3) 5 步执行链路时间线(复用 [SignalTimeline]),可读出执行耗时。
 */
export function SignalDetailDialog({ signal, onClose }: Props) {
  const symbolsQ = useQuery({ queryKey: ['admin', 'symbols'], queryFn: adminSymbols.list });
  const symbolName = signal
    ? symbolsQ.data?.find((s) => s.symbolId === signal.symbolId)?.symbolName ?? signal.symbolId
    : '';

  return (
    <Dialog open={!!signal} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        {signal && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                跟单流水详情
                <Pill tone={STATUS_TONE[signal.status]}>{STATUS_LABEL[signal.status]}</Pill>
              </DialogTitle>
              <DialogDescription className="num">
                {fmtTime(signal.createdAt)} · {symbolName} · 信号 #{signal.id}
              </DialogDescription>
            </DialogHeader>

            <Section title="雇员单(虚拟意图)">
              <KV k="用户ID"><span className="num">{signal.userId}</span></KV>
              <KV k="方向">
                <Pill tone={signal.userSide === 'BUY' ? 'up' : 'down'}>{signal.userSide}</Pill>
              </KV>
              <KV k="手数"><span className="num">{lots(signal.userVolume)}</span></KV>
              <KV k="雇员单号"><span className="num">{signal.userOrderId ?? '—'}</span></KV>
            </Section>

            <Section title="App 用户结算">
              {signal.userClosePrice != null || signal.userNetPnl?.value != null ? (
                <>
                  <KV k="平仓价">
                    <span className="num">{signal.userClosePrice != null ? fmtNum(signal.userClosePrice, 2) : '—'}</span>
                  </KV>
                  {signal.userGrossPnl?.value != null && (
                    <KV k="毛盈亏"><MoneyText m={signal.userGrossPnl} /></KV>
                  )}
                  {signal.userServiceFee?.value != null && (
                    <KV k="交易服务费"><MoneyText m={signal.userServiceFee} tone="neutral" /></KV>
                  )}
                  {signal.userFeeAllocation?.value != null && (
                    <KV k="费用分摊"><MoneyText m={signal.userFeeAllocation} /></KV>
                  )}
                  {signal.userNetPnl?.value != null && (
                    <KV k="净盈亏"><MoneyText m={signal.userNetPnl} /></KV>
                  )}
                </>
              ) : (
                <div className="col-span-2 text-sm text-ink-3">尚未结算,暂无平仓价和净盈亏。</div>
              )}
            </Section>

            <Section title="主账户净额贡献(投影)">
              {signal.realSide ? (
                <>
                  <KV k="方向">
                    <Pill tone={signal.realSide === 'BUY' ? 'up' : 'down'}>{signal.realSide}</Pill>
                  </KV>
                  <KV k="手数">
                    <span className="num">{signal.realVolume != null ? lots(signal.realVolume) : '—'}</span>
                  </KV>
                  <KV k="模式·比例">
                    <span>{signal.followMode}</span>
                    <span className="num text-ink-3 ml-1.5">×{signal.followRatioUsed?.toFixed(2) ?? '—'}</span>
                  </KV>
                  {/* v3 netting:真实订单号/真实仓 ID 恒为空(无单一真实单对应),仅历史 v1/v2 行有值时展示 */}
                  {signal.realOrderId && (
                    <KV k="真实订单号"><span className="num">{signal.realOrderId}</span></KV>
                  )}
                  {signal.realPositionId && (
                    <KV k="真实仓 ID"><span className="num">{signal.realPositionId}</span></KV>
                  )}
                </>
              ) : (
                <div className="col-span-2 text-sm text-ink-3">
                  未在主账户下单({STATUS_LABEL[signal.status]}
                  {signal.skipReason ? ` · ${signal.skipReason}` : ''})
                </div>
              )}
            </Section>

            <div>
              <div className="mb-1 text-xs text-ink-2">执行链路(§9.8)</div>
              <SignalTimeline signal={signal} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-xs text-ink-2">{title}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-md bg-bg-3 p-3">{children}</div>
    </div>
  );
}

function KV({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-3">{k}</span>
      <span className="flex items-center text-ink-1">{children}</span>
    </div>
  );
}

/** 金额文本,按正负染色 */
function MoneyText({ m, tone = 'pnl' }: { m: Money; tone?: 'pnl' | 'neutral' }) {
  const n = moneyToNumber(m);
  const toneClass = tone === 'neutral' ? 'text-ink-1' : n > 0 ? 'text-up' : n < 0 ? 'text-down' : 'text-ink-1';
  return <span className={`num ${toneClass}`}>{fmtMoney(m, { sign: tone !== 'neutral' })}</span>;
}
